/**
 * The identifier tokens the bank-line document scorer records as a leg's
 * `matchReason`, each mapped to the catalog key holding its whole sentence.
 * Keyed by the persisted token verbatim: the token is a stored value
 * (`SuggestedAllocationLeg['reason']` in `@packages/types`, and the
 * `matchReason` column on a persisted proposal leg), so this map is the one
 * place the storage vocabulary meets the copy vocabulary.
 */
const MATCH_REASON_KEYS = {
    'invoice number': 'allocation:matchReason.invoiceNumber',
    'payment reference': 'allocation:matchReason.paymentReference',
    reference: 'allocation:matchReason.reference',
    "client's name": 'allocation:matchReason.clientName',
    "supplier's name": 'allocation:matchReason.supplierName',
    'credit note number': 'allocation:matchReason.creditNoteNumber',
    'exact amount': 'allocation:matchReason.exactAmount',
};
function isMatchReasonToken(reason) {
    // `hasOwnProperty.call` rather than `Object.hasOwn`: the frontends compile
    // this source against an ES2020 lib, and an inherited name ('constructor')
    // must not read as a token.
    return Object.prototype.hasOwnProperty.call(MATCH_REASON_KEYS, reason);
}
/**
 * The sentence a user sees for a stored allocation `matchReason`.
 *
 * Two shapes reach a stored reason, and both frontends must read them the
 * same way - which is why this lives here rather than in either app:
 *
 *   - A **document** match records only the identifier token that
 *     corroborated it (`'invoice number'`, `'reference'`, ...). The token is
 *     a lookup key into the `allocation:matchReason` catalog, never a noun
 *     composed into a sentence here - each dialect stores the whole sentence.
 *   - A **history-derived** suggestion records a complete sentence, because
 *     its explanation carries a count ("...allocated to this account 6 times
 *     before") no fixed token could express. It passes through unchanged.
 *
 * Takes the `CopyFn` rather than resolving one: like every plain formatter
 * over copy, the calling component resolves the account's dialect and passes
 * it through.
 */
export function describeAllocationMatchReason(copy, reason) {
    return isMatchReasonToken(reason) ? copy(MATCH_REASON_KEYS[reason]) : reason;
}
